import React, { useState, useEffect, useMemo, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { useSelector, useDispatch } from "react-redux"
import moment from "moment"
import { toNumber } from "lodash"
import {
  Box,
  Grid,
  TextField,
  Button,
  IconButton,
  Typography,
  Autocomplete,
  Drawer,
  InputAdornment,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  Tooltip,
  CircularProgress,
  Collapse,
  Switch,
  FormControlLabel,
} from "@mui/material"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment"

import AddIcon from "@mui/icons-material/Add"
import DeleteIcon from "@mui/icons-material/Delete"
import SearchIcon from "@mui/icons-material/Search"
import CloseIcon from "@mui/icons-material/Close"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import HistoryIcon from "@mui/icons-material/History"
import HandshakeIcon from "@mui/icons-material/Handshake"
import WarningAmberIcon from "@mui/icons-material/WarningAmber"
import CheckIcon from "@mui/icons-material/Check"
import SaveIcon from "@mui/icons-material/Save"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import ExpandLessIcon from "@mui/icons-material/ExpandLess"
import NotesIcon from "@mui/icons-material/Notes"
import PublicIcon from "@mui/icons-material/Public"

// EU country codes (VAT prefixes) - excluding Greece (EL)
const EU_VAT_PREFIXES = [
  "AT", // Austria
  "BE", // Belgium
  "BG", // Bulgaria
  "HR", // Croatia
  "CY", // Cyprus
  "CZ", // Czech Republic
  "DK", // Denmark
  "EE", // Estonia
  "FI", // Finland
  "FR", // France
  "DE", // Germany
  "HU", // Hungary
  "IE", // Ireland
  "IT", // Italy
  "LV", // Latvia
  "LT", // Lithuania
  "LU", // Luxembourg
  "MT", // Malta
  "NL", // Netherlands
  "PL", // Poland
  "PT", // Portugal
  "RO", // Romania
  "SK", // Slovakia
  "SI", // Slovenia
  "ES", // Spain
  "SE", // Sweden
]

// Check if VAT number is from EU (non-Greek)
const isEuVatNumber = (vatNumber) => {
  if (!vatNumber || typeof vatNumber !== "string") return false
  const cleanVat = vatNumber.trim().toUpperCase()
  // Check if starts with any EU country code (not EL for Greece)
  return EU_VAT_PREFIXES.some((prefix) => cleanVat.startsWith(prefix))
}

import { getCustomers as onGetCustomers } from "store/actions"
import { getInvoices as onGetInvoices } from "store/actions"
import { getProducts, postOrder, getMaterials, getCustOrders, checkOrderStock } from "helpers/api_helper"
import ProductModal from "pages/Products/costCalculatorModal"
import MaterialModal from "./materialsum"

const MuiOrderCreate = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const dispatch = useDispatch()

  document.title = t("Create Order") + " | FlexFlow ERP"

  const { customers } = useSelector((state) => ({ customers: state.customers.customers }))

  const [customerid, setCustomerid] = useState("")
  const [customer, setCustomer] = useState(null)
  const [customerOrders, setCustomerOrders] = useState([])
  const [description, setDescription] = useState("")
  const [driverDescription, setDriverDescription] = useState("")
  const [startDate, setStartDate] = useState(moment())
  const [extraAddresses, setExtraAddresses] = useState([])
  const [selectedAddressIndex, setSelectedAddressIndex] = useState(0)

  const [productsList, setProductsList] = useState([])
  const [products, setProducts] = useState([])
  const [loanProducts, setLoanProducts] = useState([])
  const [services, setServices] = useState([])
  const [stockStatus, setStockStatus] = useState([])
  const [search, setSearch] = useState("")

  const [productDrawerOpen, setProductDrawerOpen] = useState(false)
  const [activeTab, setActiveTab] = useState("invoiced")
  const [notesExpanded, setNotesExpanded] = useState(false)
  const [historyModalOpen, setHistoryModalOpen] = useState(false)
  const [historyProducts, setHistoryProducts] = useState([])
  const [materialsModalOpen, setMaterialsModalOpen] = useState(false)
  const [materialsStocks, setMaterialsStocks] = useState([])
  const [costModalOpen, setCostModalOpen] = useState(false)
  const [selectedProduct, setSelectedProduct] = useState(null)
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)
  const [isIntraCommunity, setIsIntraCommunity] = useState(false)

  const sumArray = (arr, key) => arr.reduce((acc, item) => acc + Number(item[key] || 0), 0)

  // Check if customer qualifies for intra-community transaction (EU VAT, non-Greek)
  const customerHasEuVat = useMemo(() => {
    return customer && isEuVatNumber(customer.vat)
  }, [customer])

  const orderTotals = useMemo(() => {
    const totalPrdAmmount = sumArray(products, "totalPrdAmmount")
    // When intra-community is enabled, VAT is 0
    const totalVat = isIntraCommunity ? 0 : sumArray(products, "vatProducts")
    const totalQuantity = sumArray(products, "quantity")
    return {
      totalVat: Number(totalVat).toFixed(2),
      totalProductsAmmount: totalPrdAmmount,
      totalItems: Number(totalQuantity),
      totalOrder: (Number(totalPrdAmmount) + Number(totalVat)).toFixed(2),
    }
  }, [products, isIntraCommunity])

  useEffect(() => {
    dispatch(onGetCustomers())
    // Load products once on mount
    getProducts(null, null).then((res) => setProductsList(res || []))
  }, [dispatch])

  // Filter products - use useMemo for better performance
  const filteredProducts = useMemo(() => {
    if (!search) return productsList
    const searchLower = search.toLowerCase()
    return productsList.filter((product) =>
      (product.sku || "").toLowerCase().includes(searchLower) ||
      (product.name || "").toLowerCase().includes(searchLower) ||
      (product.provsku || "").toLowerCase().includes(searchLower) ||
      (product.category || "").toLowerCase().includes(searchLower)
    )
  }, [search, productsList])

  useEffect(() => {
    if (customerid) {
      getCustOrders(customerid).then((res) => setCustomerOrders(res))
      const selectedCustomer = customers.find((c) => c.id === customerid)
      setCustomer(selectedCustomer)
      // Reset intra-community toggle when customer changes
      setIsIntraCommunity(false)
      // Build addresses list - main address + any extra addresses
      const allAddresses = []

      // Add main address first
      if (selectedCustomer?.address) {
        allAddresses.push({
          address: selectedCustomer.address,
          city: selectedCustomer.city || "",
          postcode: selectedCustomer.postcode || "",
          isMain: true
        })
      }

      // Add extra addresses
      if (selectedCustomer?.extra_address) {
        try {
          const parsedAddresses = JSON.parse(selectedCustomer.extra_address)
          if (Array.isArray(parsedAddresses)) {
            parsedAddresses.forEach(addr => {
              allAddresses.push({ ...addr, isMain: false })
            })
          }
        } catch (e) {
          console.error("Error parsing extra addresses:", e)
        }
      }

      setExtraAddresses(allAddresses)
      setSelectedAddressIndex(0)
    }
  }, [customerid, customers])

  useEffect(() => {
    let cancelled = false
    const handle = setTimeout(() => {
      if (products.length > 0) {
        checkOrderStock(products).then((res) => {
          if (cancelled) return
          const noStock = res.filter((stk) => stk.stockStatus === "STOCK-FAULT")
          setStockStatus(noStock.length ? noStock : [])
        })
      } else {
        setStockStatus([])
      }
    }, 300)
    return () => { cancelled = true; clearTimeout(handle) }
  }, [products])

  const addProduct = useCallback((product) => {
    const newProduct = { ...product, quantity: 0, totalPrdAmmount: 0, vatProducts: 0 }
    setProducts((prev) => [...prev, newProduct])
    setProductDrawerOpen(false)
    setSearch("")
  }, [])

  const updateProduct = (index, field, value) => {
    const newArr = [...products]
    newArr[index][field] = value
    if (field === "quantity" || field === "salesprice") {
      const qty = parseFloat(newArr[index].quantity) || 0
      const price = parseFloat(newArr[index].salesprice) || 0
      const vat = parseFloat(newArr[index].vat) || 0
      newArr[index].totalPrdAmmount = (qty * price).toFixed(2)
      newArr[index].vatProducts = (qty * price * (vat / 100)).toFixed(2)
    }
    setProducts(newArr)
  }

  const removeProduct = (index) => setProducts((prev) => prev.filter((_, i) => i !== index))

  const removeLoanProduct = (index) => setLoanProducts((prev) => prev.filter((_, i) => i !== index))

  const checkHistory = (prd) => {
    setHistoryProducts([])
    const history = []
    customerOrders.forEach((order) => {
      try {
        const orderProducts = JSON.parse(order.products)
        const found = orderProducts.find((p) => p.id === prd.id)
        if (found) {
          history.push({
            order: order.id,
            created: order.created,
            productprice: found.salesprice,
            productquantity: found.quantity,
            productname: prd.name,
          })
        }
      } catch (e) {}
    })
    setHistoryProducts(history)
    setHistoryModalOpen(true)
  }

  const checkIfLoans = (prd) => {
    try {
      const lendProduct = JSON.parse(prd.requirements)
      if (lendProduct?.length > 0) {
        return lendProduct[0].materials.some((m) => m.canlended === true)
      }
    } catch (e) {}
    return false
  }

  const shouldShowLendButton = (prd) => {
    try {
      const lendProduct = JSON.parse(prd.requirements)
      if (lendProduct?.length > 0) {
        const mats = lendProduct[0].materials
        const canlend = mats.find((m) => m.canlended === true)
        if (canlend) {
          // Check if already lent for this product
          const alreadyLended = loanProducts.some((lp) => lp.id === canlend.id && lp.originid === prd.id)
          return !alreadyLended
        }
      }
    } catch (e) {}
    return false
  }

  const handleLendProduct = async (prd, index) => {
    try {
      const lendProduct = JSON.parse(prd.requirements)
      if (lendProduct?.length > 0) {
        const mats = lendProduct[0].materials
        const canlend = mats.find((m) => m.canlended === true)
        if (canlend) {
          // Create loan product with quantity and origin
          const loanItem = {
            ...canlend,
            quantity: prd.quantity,
            originid: prd.id,
          }

          // Add to loan products
          setLoanProducts((prev) => [...prev, loanItem])

          // Update product cost price by subtracting the lent material cost
          const materialData = await getProducts(canlend.id, null)
          if (materialData?.[0]) {
            const newArr = [...products]
            newArr[index].costprice = (parseFloat(prd.costprice) - parseFloat(materialData[0].costprice || 0)).toFixed(2)
            setProducts(newArr)
          }
        }
      }
    } catch (e) {
      console.error("Error lending product:", e)
    }
  }

  const createOrder = async (redirectTo) => {
    if (!customerid || products.length === 0) return
    setLoading(true)
    const status = stockStatus.length > 0 ? "PENDING-STOCK" : "STOCK-OK"
    const payload = {
      resource: [{
        name: customer?.name,
        label: customer?.label,
        delivery_address: JSON.stringify(extraAddresses[selectedAddressIndex] || null),
        expected_delivery_date: startDate.format("YYYY-MM-DD HH:mm:ss"),
        customer_id: toNumber(customerid),
        description,
        driverinfo: driverDescription,
        status,
        products: JSON.stringify(products),
        loans: JSON.stringify(loanProducts),
        services: JSON.stringify(services),
        totals: JSON.stringify(orderTotals),
        is_intra_community: isIntraCommunity,
      }],
    }
    try {
      const res = await postOrder(payload)
      if (res?.error) {
        setError(res.error)
      } else if (res?.resource?.[0]) {
        dispatch(onGetInvoices())
        if (redirectTo === "detail") {
          navigate(`/order-detail/${res.resource[0].id}`)
        } else {
          resetForm()
        }
      }
    } catch (e) {
      setError(e.message || "Error creating order")
    } finally {
      setLoading(false)
    }
  }

  const createOffer = async () => {
    if (!customerid || products.length === 0) return
    setLoading(true)
    const payload = {
      resource: [{
        name: customer?.name,
        expected_delivery_date: startDate.format("YYYY-MM-DD HH:mm:ss"),
        customer_id: toNumber(customerid),
        description,
        status: "OFFER",
        products: JSON.stringify(products),
        loans: JSON.stringify(loanProducts),
        services: JSON.stringify(services),
        totals: JSON.stringify(orderTotals),
        is_intra_community: isIntraCommunity,
      }],
    }
    try {
      const res = await postOrder(payload)
      if (res?.error) {
        setError(res.error)
      } else if (res?.resource?.[0]) {
        navigate("/Orders")
        dispatch(onGetInvoices())
      }
    } catch (e) {
      setError(e.message || "Error creating offer")
    } finally {
      setLoading(false)
    }
  }

  const resetForm = () => {
    setCustomerid("")
    setCustomer(null)
    setDescription("")
    setDriverDescription("")
    setStartDate(moment())
    setProducts([])
    setLoanProducts([])
    setStockStatus([])
    setError("")
    setActiveTab("invoiced")
    setIsIntraCommunity(false)
  }

  return (
    <Box sx={{ bgcolor: "#f8fafc", minHeight: "100vh" }}>
      {/* Header */}
      <Box sx={{ bgcolor: "#fff", borderBottom: "1px solid #e5e7eb", px: 2, py: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <IconButton size="small" onClick={() => navigate("/orders")} sx={{ color: "#6b7280" }}>
              <ArrowBackIcon fontSize="small" />
            </IconButton>
            <Typography variant="body2" color="#6b7280">{t("Orders")}</Typography>
            <Typography variant="body2" color="#9ca3af">/</Typography>
            <Typography variant="body2" fontWeight={500} color="#111827">{t("Create Order")}</Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={createOffer}
              disabled={!customerid || products.length === 0 || loading}
              sx={{ textTransform: "none", fontSize: "0.8125rem" }}
            >
              {t("Save as Offer")}
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
              onClick={() => createOrder("detail")}
              disabled={!customerid || products.length === 0 || loading}
              sx={{ textTransform: "none", fontSize: "0.8125rem", boxShadow: "none" }}
            >
              {t("Create Order")}
            </Button>
          </Box>
        </Box>

        {/* Alerts in header */}
        {stockStatus.length > 0 && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, px: 1, py: 0.25, bgcolor: "#fef3c7", borderRadius: 1 }}>
            <WarningAmberIcon sx={{ fontSize: 14, color: "#f59e0b" }} />
            <Typography variant="caption" color="#92400e" sx={{ fontSize: "0.75rem" }}>
              {t("Stock Alert")}: {stockStatus.map((s) => s.product.name).join(", ")}
            </Typography>
          </Box>
        )}
        {stockStatus.length === 0 && products.length > 0 && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, px: 1, py: 0.25, bgcolor: "#dcfce7", borderRadius: 1 }}>
            <CheckIcon sx={{ fontSize: 14, color: "#16a34a" }} />
            <Typography variant="caption" color="#166534" sx={{ fontSize: "0.75rem" }}>
              {t("Stock OK")}
            </Typography>
          </Box>
        )}
        {error && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, px: 1, py: 0.25, bgcolor: "#fef2f2", borderRadius: 1, cursor: "pointer" }} onClick={() => setError("")}>
            <Typography variant="caption" color="#dc2626" sx={{ fontSize: "0.75rem" }}>
              {error}
            </Typography>
            <CloseIcon sx={{ fontSize: 12, color: "#dc2626" }} />
          </Box>
        )}
      </Box>

      {/* Customer & Date Row */}
      <Box sx={{ px: 2, py: 1, bgcolor: "#fff", borderBottom: "1px solid #e5e7eb" }}>
        <Grid container spacing={1} alignItems="center">
          <Grid item xs={12} sm={3}>
            <Typography variant="caption" color="#6b7280" sx={{ fontSize: "0.7rem", display: "block", mb: 0.25 }}>
              {t("Customer")}
            </Typography>
            <Autocomplete
              options={customers || []}
              getOptionLabel={(option) => `${option.name}${option.label ? ` (${option.label})` : ""}`}
              value={customer}
              onChange={(e, newValue) => setCustomerid(newValue?.id || "")}
              size="small"
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={t("Select...")}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      fontSize: "0.8125rem",
                      "& fieldset": { borderColor: "#e5e7eb" },
                    },
                  }}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <SearchIcon sx={{ fontSize: 16, color: "#9ca3af" }} />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Box>
                    <Typography variant="body2" sx={{ fontSize: "0.8125rem" }}>{option.name}</Typography>
                    <Typography variant="caption" color="#6b7280">{option.label} | {option.phone}</Typography>
                  </Box>
                </li>
              )}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
            />
          </Grid>
          <Grid item xs={6} sm={2}>
            <Typography variant="caption" color="#6b7280" sx={{ fontSize: "0.7rem", display: "block", mb: 0.25 }}>
              {t("Expected Delivery")}
            </Typography>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                format="DD/MM/YYYY"
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                    sx: { "& .MuiOutlinedInput-root": { fontSize: "0.8125rem" } },
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={6} sm={4}>
            <Typography variant="caption" color="#6b7280" sx={{ fontSize: "0.7rem", display: "block", mb: 0.25 }}>
              {t("Shipping Address")}
            </Typography>
            <FormControl fullWidth size="small">
              <Select
                value={selectedAddressIndex}
                onChange={(e) => setSelectedAddressIndex(e.target.value)}
                displayEmpty
                disabled={!customer || extraAddresses.length === 0}
                sx={{ fontSize: "0.8125rem" }}
              >
                {extraAddresses.length === 0 ? (
                  <MenuItem value={0} sx={{ fontSize: "0.8125rem" }}>
                    {t("No address available")}
                  </MenuItem>
                ) : (
                  extraAddresses.map((addr, index) => (
                    <MenuItem key={index} value={index} sx={{ fontSize: "0.8125rem" }}>
                      {addr.address || "N/A"}, {addr.city || "N/A"}
                      {addr.isMain && <Chip label={t("Main")} size="small" sx={{ ml: 1, height: 16, fontSize: "0.65rem" }} />}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </Grid>
          {/* Intra-community Transaction Toggle - only shown for EU VAT customers */}
          {customerHasEuVat && (
            <Grid item xs={12} sm="auto">
              <Tooltip title={t("Apply 0% VAT for intra-community transactions within EU")}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    px: 1.5,
                    py: 0.5,
                    bgcolor: isIntraCommunity ? "#dbeafe" : "#f3f4f6",
                    borderRadius: 1,
                    border: isIntraCommunity ? "1px solid #3b82f6" : "1px solid #e5e7eb",
                    transition: "all 0.2s ease",
                  }}
                >
                  <PublicIcon sx={{ fontSize: 16, color: isIntraCommunity ? "#3b82f6" : "#6b7280" }} />
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={isIntraCommunity}
                        onChange={(e) => setIsIntraCommunity(e.target.checked)}
                        sx={{
                          "& .MuiSwitch-switchBase.Mui-checked": { color: "#3b82f6" },
                          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: "#3b82f6" },
                        }}
                      />
                    }
                    label={
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: "0.7rem",
                          fontWeight: isIntraCommunity ? 600 : 400,
                          color: isIntraCommunity ? "#1d4ed8" : "#6b7280",
                        }}
                      >
                        {t("Intra-community")}
                      </Typography>
                    }
                    sx={{ m: 0 }}
                  />
                  {isIntraCommunity && (
                    <Chip
                      label="0% VAT"
                      size="small"
                      sx={{ height: 18, fontSize: "0.6rem", bgcolor: "#dcfce7", color: "#166534", fontWeight: 600 }}
                    />
                  )}
                </Box>
              </Tooltip>
            </Grid>
          )}
          <Grid item xs="auto" sx={{ ml: "auto" }}>
            <Button
              size="small"
              variant="text"
              startIcon={<NotesIcon sx={{ fontSize: 14 }} />}
              endIcon={notesExpanded ? <ExpandLessIcon sx={{ fontSize: 14 }} /> : <ExpandMoreIcon sx={{ fontSize: 14 }} />}
              onClick={() => setNotesExpanded(!notesExpanded)}
              sx={{
                textTransform: "none",
                fontSize: "0.75rem",
                color: (description || driverDescription) ? "#3b82f6" : "#6b7280",
                minWidth: "auto",
                mt: 2,
              }}
            >
              {t("Notes")}
              {(description || driverDescription) && (
                <Box component="span" sx={{ ml: 0.5, width: 6, height: 6, borderRadius: "50%", bgcolor: "#3b82f6" }} />
              )}
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* Notes Panel */}
      <Collapse in={notesExpanded}>
        <Box sx={{ px: 2, py: 1, bgcolor: "#fff", borderBottom: "1px solid #e5e7eb" }}>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small"
                fullWidth
                placeholder={t("Order notes...")}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                multiline
                rows={2}
                sx={{ "& .MuiOutlinedInput-root": { fontSize: "0.8125rem" } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small"
                fullWidth
                placeholder={t("Driver instructions...")}
                value={driverDescription}
                onChange={(e) => setDriverDescription(e.target.value)}
                multiline
                rows={2}
                sx={{ "& .MuiOutlinedInput-root": { fontSize: "0.8125rem" } }}
              />
            </Grid>
          </Grid>
        </Box>
      </Collapse>

      {/* Products Table */}
      <Box sx={{ px: 2, py: 1 }}>
        <Box sx={{ bgcolor: "#fff", border: "1px solid #e5e7eb", borderRadius: 1, overflow: "hidden" }}>
          {/* Tabs Header */}
          <Box sx={{ px: 1.5, py: 0.5, borderBottom: "1px solid #e5e7eb", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0 }}>
              <Button
                size="small"
                onClick={() => setActiveTab("invoiced")}
                sx={{
                  textTransform: "none",
                  fontSize: "0.8rem",
                  py: 0.5,
                  px: 2,
                  minWidth: "auto",
                  borderRadius: 0,
                  borderBottom: activeTab === "invoiced" ? "2px solid #3b82f6" : "2px solid transparent",
                  bgcolor: "transparent",
                  color: activeTab === "invoiced" ? "#3b82f6" : "#6b7280",
                  "&:hover": { bgcolor: "#f3f4f6" },
                }}
              >
                {t("Invoiced")}
              </Button>
              {loanProducts.length > 0 && (
                <Button
                  size="small"
                  onClick={() => setActiveTab("loans")}
                  sx={{
                    textTransform: "none",
                    fontSize: "0.8rem",
                    py: 0.5,
                    px: 2,
                    minWidth: "auto",
                    borderRadius: 0,
                    borderBottom: activeTab === "loans" ? "2px solid #8b5cf6" : "2px solid transparent",
                    bgcolor: activeTab === "loans" ? "#F1D9F3" : "transparent",
                    color: activeTab === "loans" ? "#8b5cf6" : "#6b7280",
                    "&:hover": { bgcolor: "#F1D9F3" },
                  }}
                >
                  {t("Loan Stock")}
                </Button>
              )}
            </Box>
            <Box sx={{ display: "flex", gap: 0.5 }}>
              {activeTab === "invoiced" && (
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => setProductDrawerOpen(true)}
                  disabled={!customerid}
                  sx={{ textTransform: "none", fontSize: "0.75rem", py: 0.25, boxShadow: "none" }}
                >
                  {t("Add Products")}
                </Button>
              )}
            </Box>
          </Box>

          {/* Invoiced Products Table */}
          {activeTab === "invoiced" && (
            <>

          {/* Table - Matching Original OrderTable columns */}
          <TableContainer sx={{ maxHeight: "calc(100vh - 320px)" }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ bgcolor: "#f9fafb", py: 0.5, fontSize: "0.65rem", fontWeight: 600, color: "#6b7280", textTransform: "uppercase", width: 30 }}>#</TableCell>
                  <TableCell sx={{ bgcolor: "#f9fafb", py: 0.5, fontSize: "0.65rem", fontWeight: 600, color: "#6b7280", textTransform: "uppercase" }}>{t("NAME")}</TableCell>
                  <TableCell sx={{ bgcolor: "#f9fafb", py: 0.5, fontSize: "0.65rem", fontWeight: 600, color: "#6b7280", textTransform: "uppercase", width: 70 }}>{t("SKU")}</TableCell>
                  <TableCell sx={{ bgcolor: "#f9fafb", py: 0.5, fontSize: "0.65rem", fontWeight: 600, color: "#6b7280", textTransform: "uppercase", width: 55 }} align="right">{t("LCOST")}</TableCell>
                  <TableCell sx={{ bgcolor: "#f9fafb", py: 0.5, fontSize: "0.65rem", fontWeight: 600, color: "#6b7280", textTransform: "uppercase", width: 65 }} align="right">{t("PRICE")}</TableCell>
                  <TableCell sx={{ bgcolor: "#f9fafb", py: 0.5, fontSize: "0.65rem", fontWeight: 600, color: "#6b7280", textTransform: "uppercase", width: 40 }} align="center">{t("VAT")}</TableCell>
                  <TableCell sx={{ bgcolor: "#f9fafb", py: 0.5, fontSize: "0.65rem", fontWeight: 600, color: "#6b7280", textTransform: "uppercase", width: 50 }}>{t("UNITS")}</TableCell>
                  <TableCell sx={{ bgcolor: "#f9fafb", py: 0.5, fontSize: "0.65rem", fontWeight: 600, color: "#6b7280", textTransform: "uppercase", width: 55 }} align="center">{t("QTY")}</TableCell>
                  <TableCell sx={{ bgcolor: "#f9fafb", py: 0.5, fontSize: "0.65rem", fontWeight: 600, color: "#6b7280", textTransform: "uppercase", width: 60 }} align="right">{t("TOTAL")}</TableCell>
                  <TableCell sx={{ bgcolor: "#f9fafb", py: 0.5, fontSize: "0.65rem", fontWeight: 600, color: "#6b7280", textTransform: "uppercase", width: 50 }} align="right">{t("VAT€")}</TableCell>
                  <TableCell sx={{ bgcolor: "#f9fafb", py: 0.5, fontSize: "0.65rem", fontWeight: 600, color: "#6b7280", textTransform: "uppercase", width: 60 }} align="right">{t("Inc-VAT")}</TableCell>
                  <TableCell sx={{ bgcolor: "#f9fafb", py: 0.5, fontSize: "0.65rem", fontWeight: 600, color: "#6b7280", textTransform: "uppercase", width: 50 }} align="right">{t("Stock")}</TableCell>
                  <TableCell sx={{ bgcolor: "#f9fafb", py: 0.5, fontSize: "0.65rem", fontWeight: 600, color: "#6b7280", textTransform: "uppercase", width: 50 }} align="center">{t("ACT")}</TableCell>
                  <TableCell sx={{ bgcolor: "#f9fafb", py: 0.5, fontSize: "0.65rem", fontWeight: 600, color: "#6b7280", textTransform: "uppercase", width: 50 }} align="center">{t("LOAN")}</TableCell>
                  <TableCell sx={{ bgcolor: "#f9fafb", py: 0.5, fontSize: "0.65rem", fontWeight: 600, color: "#6b7280", textTransform: "uppercase", width: 50 }} align="center">{t("DEL")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {products.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={15} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="#9ca3af">{customerid ? t("Click Add to add products") : t("Select a customer first")}</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  products.map((prd, index) => (
                    <TableRow key={index} sx={{ "&:hover": { bgcolor: "#f9fafb" } }}>
                      <TableCell sx={{ py: 0.25, fontSize: "0.75rem", color: "#6b7280" }}>{index + 1}</TableCell>
                      <TableCell sx={{ py: 0.25 }}>
                        <Typography variant="body2" fontWeight={500} color="#111827" sx={{ fontSize: "0.75rem" }}>{prd.name}</Typography>
                      </TableCell>
                      <TableCell sx={{ py: 0.25 }}>
                        <Typography variant="body2" color="#6b7280" sx={{ fontSize: "0.7rem" }}>{prd.sku}</Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ py: 0.25 }}>
                        <Typography
                          variant="body2"
                          sx={{ fontSize: "0.75rem", cursor: "pointer", color: prd.costprice > prd.salesprice ? "#dc2626" : "#16a34a" }}
                          onClick={() => { setSelectedProduct(prd); setCostModalOpen(true) }}
                        >
                          {parseFloat(prd.costprice || 0).toFixed(2)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ py: 0.25 }}>
                        <TextField
                          type="number"
                          value={prd.salesprice || ""}
                          onChange={(e) => updateProduct(index, "salesprice", e.target.value)}
                          size="small"
                          inputProps={{ step: "0.01", min: 0, style: { textAlign: "right", fontSize: "0.7rem", padding: "2px 4px" } }}
                          sx={{ width: 55, "& .MuiOutlinedInput-root": { bgcolor: prd.costprice > prd.salesprice ? "#fef2f2" : "#f0fdf4" } }}
                        />
                      </TableCell>
                      <TableCell align="center" sx={{ py: 0.25 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: "0.7rem",
                            color: isIntraCommunity ? "#16a34a" : "#6b7280",
                            fontWeight: isIntraCommunity ? 600 : 400,
                          }}
                        >
                          {isIntraCommunity ? "0%" : `${prd.vat}%`}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 0.25 }}>
                        <Typography variant="body2" color="#6b7280" sx={{ fontSize: "0.7rem" }}>{prd.unit || "-"}</Typography>
                      </TableCell>
                      <TableCell align="center" sx={{ py: 0.25 }}>
                        <TextField
                          type="number"
                          value={prd.quantity || ""}
                          onChange={(e) => updateProduct(index, "quantity", e.target.value)}
                          size="small"
                          inputProps={{ step: "0.01", min: 0, style: { textAlign: "center", fontSize: "0.7rem", padding: "2px 4px" } }}
                          sx={{ width: 50 }}
                        />
                      </TableCell>
                      <TableCell align="right" sx={{ py: 0.25 }}>
                        <Typography variant="body2" color="#111827" sx={{ fontSize: "0.75rem" }}>
                          {parseFloat(prd.totalPrdAmmount || 0).toFixed(2)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ py: 0.25 }}>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          sx={{
                            fontSize: "0.75rem",
                            color: isIntraCommunity ? "#16a34a" : "#111827",
                          }}
                        >
                          {isIntraCommunity ? "0.00" : parseFloat(prd.vatProducts || 0).toFixed(2)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ py: 0.25 }}>
                        <Typography variant="body2" fontWeight={600} color="#111827" sx={{ fontSize: "0.75rem" }}>
                          {isIntraCommunity
                            ? parseFloat(prd.totalPrdAmmount || 0).toFixed(2)
                            : (parseFloat(prd.totalPrdAmmount || 0) + parseFloat(prd.vatProducts || 0)).toFixed(2)
                          }
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ py: 0.25 }}>
                        <Typography
                          variant="body2"
                          sx={{ fontSize: "0.75rem", fontWeight: 600, color: prd.stock >= (prd.quantity || 0) ? "#16a34a" : "#dc2626" }}
                        >
                          {(prd.stock - (prd.quantity || 0)).toFixed(0)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center" sx={{ py: 0.25 }}>
                        <Button size="small" variant="text" onClick={() => checkHistory(prd)} sx={{ minWidth: 0, p: 0.25, fontSize: "0.65rem", textTransform: "none", color: "#6b7280" }}>
                          {t("History")}
                        </Button>
                      </TableCell>
                      <TableCell align="center" sx={{ py: 0.25 }}>
                        {prd.quantity > 0 && checkIfLoans(prd) && shouldShowLendButton(prd) && (
                          <Button
                            size="small"
                            variant="text"
                            onClick={() => handleLendProduct(prd, index)}
                            sx={{ minWidth: 0, p: 0.25, fontSize: "0.65rem", textTransform: "none", color: "#8b5cf6" }}
                          >
                            {t("Lend")}
                          </Button>
                        )}
                      </TableCell>
                      <TableCell align="center" sx={{ py: 0.25 }}>
                        <Button size="small" variant="text" onClick={() => removeProduct(index)} sx={{ minWidth: 0, p: 0.25, fontSize: "0.65rem", textTransform: "none", color: "#dc2626" }}>
                          {t("Delete")}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Totals Footer */}
          {products.length > 0 && (
            <Box sx={{ px: 1.5, py: 1, borderTop: "1px solid #e5e7eb", bgcolor: "#f9fafb" }}>
              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="body2" color="#6b7280" sx={{ fontSize: "0.8125rem" }}>{t("Items")}:</Typography>
                  <Typography variant="body2" fontWeight={600} color="#111827" sx={{ fontSize: "0.8125rem" }}>{orderTotals.totalItems}</Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="body2" color="#6b7280" sx={{ fontSize: "0.8125rem" }}>{t("Subtotal")}:</Typography>
                  <Typography variant="body2" fontWeight={600} color="#111827" sx={{ fontSize: "0.8125rem" }}>{Number(orderTotals.totalProductsAmmount).toFixed(2)}€</Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="body2" color="#6b7280" sx={{ fontSize: "0.8125rem" }}>{t("VAT")}:</Typography>
                  <Typography variant="body2" fontWeight={600} color="#111827" sx={{ fontSize: "0.8125rem" }}>{orderTotals.totalVat}€</Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="body2" color="#6b7280" sx={{ fontSize: "0.8125rem" }}>{t("Total")}:</Typography>
                  <Typography variant="body1" fontWeight={700} color="#3b82f6" sx={{ fontSize: "1rem" }}>{orderTotals.totalOrder}€</Typography>
                </Box>
              </Box>
            </Box>
          )}
            </>
          )}

          {/* Loan Products Table - Matching Original LoanorderTable */}
          {activeTab === "loans" && (
            <>
              <TableContainer sx={{ maxHeight: "calc(100vh - 320px)", bgcolor: "#F1D9F3" }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ bgcolor: "#F1D9F3", py: 0.5, fontSize: "0.65rem", fontWeight: 600, color: "#6b7280", textTransform: "uppercase", width: 40 }}>{t("ITEM")}</TableCell>
                      <TableCell sx={{ bgcolor: "#F1D9F3", py: 0.5, fontSize: "0.65rem", fontWeight: 600, color: "#6b7280", textTransform: "uppercase" }}>{t("NAME")}</TableCell>
                      <TableCell sx={{ bgcolor: "#F1D9F3", py: 0.5, fontSize: "0.65rem", fontWeight: 600, color: "#6b7280", textTransform: "uppercase", width: 70 }}>{t("ORIGIN")}</TableCell>
                      <TableCell sx={{ bgcolor: "#F1D9F3", py: 0.5, fontSize: "0.65rem", fontWeight: 600, color: "#6b7280", textTransform: "uppercase", width: 80 }}>{t("SKU")}</TableCell>
                      <TableCell sx={{ bgcolor: "#F1D9F3", py: 0.5, fontSize: "0.65rem", fontWeight: 600, color: "#6b7280", textTransform: "uppercase", width: 70 }}>{t("UNITS")}</TableCell>
                      <TableCell sx={{ bgcolor: "#F1D9F3", py: 0.5, fontSize: "0.65rem", fontWeight: 600, color: "#6b7280", textTransform: "uppercase", width: 70 }}>{t("QUANTITY")}</TableCell>
                      <TableCell sx={{ bgcolor: "#F1D9F3", py: 0.5, fontSize: "0.65rem", fontWeight: 600, color: "#6b7280", textTransform: "uppercase", width: 60 }}>{t("DELETE")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loanProducts.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 4, bgcolor: "#F1D9F3" }}>
                          <Typography variant="body2" color="#9ca3af">{t("No loan products. Use Lend button on products.")}</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      loanProducts.map((prd, index) => (
                        <TableRow key={index} sx={{ bgcolor: "#F1D9F3" }}>
                          <TableCell sx={{ py: 0.25, fontSize: "0.75rem", color: "#6b7280" }}>{index + 1}</TableCell>
                          <TableCell sx={{ py: 0.25 }}>
                            <Typography variant="body2" fontWeight={500} color="#111827" sx={{ fontSize: "0.75rem" }}>{prd.name}</Typography>
                          </TableCell>
                          <TableCell sx={{ py: 0.25 }}>
                            <Typography variant="body2" color="#6b7280" sx={{ fontSize: "0.75rem" }}>{prd.originid}</Typography>
                          </TableCell>
                          <TableCell sx={{ py: 0.25 }}>
                            <Typography variant="body2" color="#6b7280" sx={{ fontSize: "0.75rem" }}>{prd.sku || "-"}</Typography>
                          </TableCell>
                          <TableCell sx={{ py: 0.25 }}>
                            <Typography variant="body2" color="#6b7280" sx={{ fontSize: "0.75rem" }}>{prd.unit || "-"}</Typography>
                          </TableCell>
                          <TableCell sx={{ py: 0.25 }}>
                            <Typography variant="body2" color="#111827" sx={{ fontSize: "0.75rem" }}>{prd.quantity}</Typography>
                          </TableCell>
                          <TableCell sx={{ py: 0.25 }}>
                            <Button size="small" variant="text" onClick={() => removeLoanProduct(index)} sx={{ minWidth: 0, p: 0.25, fontSize: "0.65rem", textTransform: "none", color: "#dc2626" }}>
                              {t("Delete")}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </Box>
      </Box>

      {/* Product Drawer */}
      <Drawer
        anchor="right"
        open={productDrawerOpen}
        onClose={() => { setProductDrawerOpen(false); setSearch("") }}
        PaperProps={{ sx: { width: { xs: "100%", sm: 380 } } }}
        ModalProps={{ keepMounted: true }}
      >
        <Box sx={{ p: 1.5, borderBottom: "1px solid #e5e7eb" }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
            <Typography variant="body1" fontWeight={600}>{t("Add Products")}</Typography>
            <IconButton size="small" onClick={() => { setProductDrawerOpen(false); setSearch("") }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <TextField
            fullWidth
            placeholder={t("Search...")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            autoFocus
            sx={{ "& .MuiOutlinedInput-root": { fontSize: "0.8125rem" } }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: "#9ca3af" }} /></InputAdornment>,
            }}
          />
          <Typography variant="caption" color="#9ca3af" sx={{ mt: 0.5, display: "block" }}>
            {filteredProducts.length} {t("products")}
          </Typography>
        </Box>
        <Box sx={{ overflow: "auto", flex: 1 }}>
          {filteredProducts.slice(0, 100).map((prd) => (
            <Box
              key={prd.id}
              onClick={() => addProduct(prd)}
              sx={{
                px: 1.5,
                py: 1,
                cursor: "pointer",
                borderBottom: "1px solid #f3f4f6",
                "&:hover": { bgcolor: "#f9fafb" },
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="body2" fontWeight={500} sx={{ fontSize: "0.8125rem" }} noWrap>{prd.name}</Typography>
                  <Typography variant="caption" color="#9ca3af" sx={{ flexShrink: 0 }}>{prd.sku}</Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 2 }}>
                  <Typography variant="caption" color="#3b82f6">{Number(prd.salesprice || 0).toFixed(2)}€</Typography>
                  <Typography variant="caption" color={prd.stock > 0 ? "#16a34a" : "#dc2626"}>
                    {t("Stock")}: {prd.stock || 0}
                  </Typography>
                </Box>
              </Box>
              <AddIcon sx={{ fontSize: 18, color: "#9ca3af", flexShrink: 0 }} />
            </Box>
          ))}
          {filteredProducts.length === 0 && (
            <Box sx={{ p: 3, textAlign: "center" }}>
              <Typography variant="body2" color="#9ca3af">{t("No products found")}</Typography>
            </Box>
          )}
          {filteredProducts.length > 100 && (
            <Box sx={{ p: 2, textAlign: "center", bgcolor: "#f9fafb" }}>
              <Typography variant="caption" color="#6b7280">
                {t("Showing first 100 results. Type to search for more.")}
              </Typography>
            </Box>
          )}
        </Box>
      </Drawer>

      {/* History Modal */}
      {historyModalOpen && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1300,
          }}
          onClick={() => setHistoryModalOpen(false)}
        >
          <Box
            sx={{ bgcolor: "#fff", borderRadius: 1, maxWidth: 400, width: "90%", maxHeight: "80vh", overflow: "auto" }}
            onClick={(e) => e.stopPropagation()}
          >
            <Box sx={{ p: 1.5, borderBottom: "1px solid #e5e7eb", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Typography variant="body1" fontWeight={600}>{t("Sales History")}</Typography>
              <IconButton size="small" onClick={() => setHistoryModalOpen(false)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontSize: "0.75rem", fontWeight: 600 }}>{t("Order")}</TableCell>
                    <TableCell align="right" sx={{ fontSize: "0.75rem", fontWeight: 600 }}>{t("Qty")}</TableCell>
                    <TableCell align="right" sx={{ fontSize: "0.75rem", fontWeight: 600 }}>{t("Price")}</TableCell>
                    <TableCell sx={{ fontSize: "0.75rem", fontWeight: 600 }}>{t("Date")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {historyProducts.map((hist, i) => (
                    <TableRow key={i}>
                      <TableCell sx={{ fontSize: "0.8125rem" }}>{hist.order}</TableCell>
                      <TableCell align="right" sx={{ fontSize: "0.8125rem" }}>{hist.productquantity}</TableCell>
                      <TableCell align="right" sx={{ fontSize: "0.8125rem" }}>{Number(hist.productprice).toFixed(2)}€</TableCell>
                      <TableCell sx={{ fontSize: "0.8125rem" }}>{moment(hist.created).format("DD/MM/YY")}</TableCell>
                    </TableRow>
                  ))}
                  {historyProducts.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 2 }}>
                        <Typography variant="body2" color="#9ca3af">{t("No history")}</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      )}

      <ProductModal isOpen={costModalOpen} toggle={() => setCostModalOpen(false)} product={selectedProduct} />
      <MaterialModal isOpen={materialsModalOpen} setisOpen={setMaterialsModalOpen} products={materialsStocks} />
    </Box>
  )
}

export default MuiOrderCreate
